/**
 * Lead Show Component
 *
 * Full contact sheet: identity header with status, classification, contact
 * channels, address, CRM/consent data, tags and interests, notes and audit
 * timestamps. Every value is labeled; empty values render as a dash.
 */

import React from 'react';
import {
  EditButton,
  ListButton,
  Show,
  TopToolbar,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';

interface LeadRecord {
  id: number;
  treatment?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  secondaryEmail?: string;
  mobilePhone?: string;
  personalMobilePhone?: string;
  landlinePhone?: string;
  phoneExtension?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  linkedInUrl?: string;
  website?: string;
  status?: string;
  preferredLanguage?: string;
  acceptsNewsletter?: boolean;
  consentDate?: string;
  consentOrigin?: string;
  notes?: string;
  companyName?: string;
  hotelName?: string;
  hotelChainName?: string;
  effectiveChainName?: string;
  hotelCategoryName?: string;
  hotelPmsSystemName?: string;
  hotelLockSystemName?: string;
  hotelPaymentProcessorName?: string;
  jobTitleName?: string;
  contactTypeName?: string;
  leadSourceName?: string;
  tagNames?: string[];
  interestNames?: string[];
  lastContactDate?: string;
  nextContactDate?: string;
  isArchived?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const Value = ({ label, value, href }: { label: string; value?: React.ReactNode; href?: string }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value
        ? href
          ? <Link href={href} target="_blank" rel="noopener noreferrer">{value}</Link>
          : value
        : '—'}
    </Typography>
  </Grid>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card sx={{ border: 1, borderColor: 'divider', mb: 2 }}>
    <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: 2.5 } }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </CardContent>
  </Card>
);

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString('es-ES') : undefined;

const LeadSheet = () => {
  const record = useRecordContext<LeadRecord>();
  const translate = useTranslate();
  if (!record) return null;

  const f = (key: string) => translate(`resources.leads.fields.${key}`);
  const t = (key: string) => translate(`resources.leads.sections.${key}`);

  const fullName = [record.treatment, record.firstName, record.lastName]
    .filter(Boolean).join(' ') || record.email || `#${record.id}`;

  return (
    <Box sx={{ maxWidth: 'lg', mx: 'auto', width: '100%', p: { xs: 1, sm: 2 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{fullName}</Typography>
        {record.status && <Chip label={record.status} color="primary" size="small" />}
        {record.acceptsNewsletter && (
          <Chip label={f('acceptsNewsletter')} color="success" size="small" variant="outlined" />
        )}
        {record.isArchived && <Chip label={f('isArchived')} color="warning" size="small" />}
      </Stack>

      <Section title={t('classification')}>
        <Grid container spacing={1.5}>
          <Value label={f('companyName')} value={record.companyName} />
          <Value label={f('jobTitleName')} value={record.jobTitleName} />
          <Value label={f('contactTypeName')} value={record.contactTypeName} />
          <Value label={f('hotelName')} value={record.hotelName} />
          <Value label={f('effectiveChainName')} value={record.effectiveChainName} />
          <Value label={f('hotelCategoryName')} value={record.hotelCategoryName} />
          <Value label={f('hotelPmsSystemName')} value={record.hotelPmsSystemName} />
          <Value label={f('hotelLockSystemName')} value={record.hotelLockSystemName} />
          <Value label={f('hotelPaymentProcessorName')} value={record.hotelPaymentProcessorName} />
          <Value label={f('leadSourceName')} value={record.leadSourceName} />
          <Value label={f('preferredLanguage')} value={record.preferredLanguage} />
        </Grid>
      </Section>

      <Section title={t('contactInfo')}>
        <Grid container spacing={1.5}>
          <Value label={f('email')} value={record.email} href={record.email ? `mailto:${record.email}` : undefined} />
          <Value label={f('secondaryEmail')} value={record.secondaryEmail} />
          <Value label={f('mobilePhone')} value={record.mobilePhone} />
          <Value label={f('personalMobilePhone')} value={record.personalMobilePhone} />
          <Value
            label={f('landlinePhone')}
            value={record.landlinePhone
              ? `${record.landlinePhone}${record.phoneExtension ? ` (ext. ${record.phoneExtension})` : ''}`
              : undefined}
          />
          <Value label={f('linkedInUrl')} value={record.linkedInUrl} href={record.linkedInUrl} />
          <Value label={f('website')} value={record.website} href={record.website} />
        </Grid>
      </Section>

      <Section title={t('location')}>
        <Grid container spacing={1.5}>
          <Value label={f('address')} value={record.address} />
          <Value label={f('city')} value={record.city} />
          <Value label={f('region')} value={record.region} />
          <Value label={f('country')} value={record.country} />
        </Grid>
      </Section>

      <Section title={t('crm')}>
        <Grid container spacing={1.5}>
          <Value label={f('lastContactDate')} value={formatDate(record.lastContactDate)} />
          <Value label={f('nextContactDate')} value={formatDate(record.nextContactDate)} />
          <Value label={f('acceptsNewsletter')} value={record.acceptsNewsletter
            ? translate('resources.leads.filters.newsletterYes')
            : translate('resources.leads.filters.newsletterNo')} />
          <Value label={f('consentDate')} value={formatDate(record.consentDate)} />
          <Value label={f('consentOrigin')} value={record.consentOrigin} />
        </Grid>
      </Section>

      <Section title={t('tagsInterests')}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {(record.tagNames ?? []).map((tag) => (
            <Chip key={`t-${tag}`} label={tag} size="small" />
          ))}
          {(record.interestNames ?? []).map((interest) => (
            <Chip key={`i-${interest}`} label={interest} size="small" variant="outlined" />
          ))}
          {(record.tagNames ?? []).length === 0 && (record.interestNames ?? []).length === 0 && (
            <Typography variant="body2">—</Typography>
          )}
        </Stack>
      </Section>

      <Section title={f('notes')}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {record.notes || '—'}
        </Typography>
      </Section>

      <Typography variant="caption" color="text.secondary">
        {f('createdAt')}: {record.createdAt ? new Date(record.createdAt).toLocaleString('es-ES') : '—'}
        {' · '}
        {f('updatedAt')}: {record.updatedAt ? new Date(record.updatedAt).toLocaleString('es-ES') : '—'}
      </Typography>
    </Box>
  );
};

const ShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton variant="contained" />
  </TopToolbar>
);

export const LeadShow = () => (
  <Show actions={<ShowActions />}>
    <LeadSheet />
  </Show>
);

export default LeadShow;
